import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { getToken } from '../../../selectors/base.selectors';
import { BaseActionTypes } from '../../../actions/base.actions';
import { EMPTY, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { State } from '../../../index';
import { HttpService } from '../../../../services/http.service';
import { GraphQlEffectService } from '../../../../services/graph-ql-effect.service';
import { environment } from '../../../../../../environments/environment';
import { concatMap } from 'rxjs/internal/operators/concatMap';
import { GetDoctorsRequest } from '../../../actions/mdqx/doctor/doctor.actions';
import { ServiceGroupActions } from '../../../actions/mdqx/doctor/service-group.actions';
import { ServiceGroupEntity } from '../../../../entities/mdqx/doctor/service-group.entity';


enum ErrorCodes {
    Load = 'Leistungen abrufen fehlgeschlagen',
    Change = 'Leistungen bearbeiten fehlgeschlagen',
    Delete = 'Leistungen löschen fehlgeschlagen',
}

@Injectable()
export class ServiceGroupEffects {
    errors$ = createEffect(() => this.actions$.pipe(
        ofType(
            ServiceGroupActions.loadFailure,
            ServiceGroupActions.changeFailure,
            ServiceGroupActions.createFailure,
            ServiceGroupActions.deleteFailure,
        ),
        map((action) => BaseActionTypes.ErrorAction({
            Payload: {
                ToasterMessage: action.ToasterMessage,
                Err: action.Errors,
            }
        }))
    ))

    Get$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ServiceGroupActions.loadRequest),
                withLatestFrom(this.store$.select(getToken)),
                switchMap(([{ Payload }, token]) => {
                    const queryString = `query {
                    servicegroup{
                        ${ServiceGroupEntity.GqlFields.join(',')}
                    }
                }`;
                    return this.gatewayHttpService.graphQl({ query: queryString }, { token, retry: true }, '', environment.mdqxGatewayApiUrl).pipe(
                        tap((res) => {
                            this.store$.dispatch(
                                res && res.servicegroup
                                    ?
                                    ServiceGroupActions.loadSuccess({
                                        Payload: res.servicegroup.map((val) => ServiceGroupEntity.fromBackend(val)),
                                    })
                                    : ServiceGroupActions.loadFailure(
                                        { ToasterMessage: ErrorCodes.Load,
                                            Errors: 'wrong response servicegroup'},
                                    ),
                            );
                        }),
                        catchError((err, caught) => {
                            this.store$.dispatch(
                                ServiceGroupActions.loadFailure({
                                    ToasterMessage: ErrorCodes.Load,
                                    Errors: err,
                                }),
                            );
                            return EMPTY;
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );

    Put$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ServiceGroupActions.changeRequest),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
                mutation{
                  changeServicegroup(id: ${Payload.id} name: "${Payload.name}"){
                  ${ServiceGroupEntity.GqlFields.join(',')}
                  }
                }
                `
                return this.gatewayHttpService.graphQl({ query: queryString }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                    map((res) => {
                        if (res?.changeServicegroup) {
                            return ServiceGroupActions.changeSuccess({Payload: ServiceGroupEntity.fromBackend(res.changeServicegroup)});
                        } else {
                            return ServiceGroupActions.changeFailure({
                                ToasterMessage: ErrorCodes.Change,
                                Errors: 'wrong response changeServicegroup',
                            });
                        }
                    }),
                    catchError((err, caught) => {
                        return of(ServiceGroupActions.changeFailure({
                            ToasterMessage: ErrorCodes.Change,
                            Errors: err,
                        }));
                    }),
                );
            }),
        ),
    );
    Delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ServiceGroupActions.deleteRequest),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
                mutation{
                  deleteServicegroup(id: ${Payload.id})
                }
                `
                return this.gatewayHttpService.graphQl({ query: queryString }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                    concatMap((res) => {
                        if (res?.deleteServicegroup) {
                            return [ServiceGroupActions.deleteSuccess({Payload: Payload.id}), new GetDoctorsRequest()];
                        } else {
                            return [ServiceGroupActions.deleteFailure({
                                ToasterMessage: ErrorCodes.Delete,
                                Errors: 'wrong response deleteServicegroup',
                            })];
                        }
                    }),
                    catchError((err, caught) => {
                        return of(ServiceGroupActions.deleteFailure({
                            ToasterMessage: ErrorCodes.Delete,
                            Errors: err,
                        }));
                    }),
                );
            }),
        ),
    );
    constructor(private actions$: Actions, private store$: Store<State>, private gatewayHttpService: HttpService, private graphQlEffectService: GraphQlEffectService) {}
}
